// src/components/layout/views/BOMView.jsx

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiRefreshCw, FiStar, FiChevronDown, FiChevronRight,
  FiBox, FiSearch, FiDownload, FiMaximize2, FiMinimize2,
  FiColumns, FiArrowUp, FiArrowDown,
  FiChevronsRight, FiChevronsLeft,
  FiPackage, FiTag, FiHash, FiDollarSign,
  FiArrowUpCircle, FiXCircle, FiCheck, FiGitBranch,
  FiActivity, FiTrendingUp, FiInfo, FiTarget,
  FiMaximize, FiMinimize
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { getAllComponents } from '../../../data/aiOntology';
import { getAlternativesForComponent, getBestAlternative, getComponentAlternativesCount } from '../../../data/componentAlternatives';

// ============================================================
// COLUMN DEFINITIONS
// ============================================================

const COLUMN_DEFINITIONS = {
  'name': { label: 'Component Name', width: 250, default: true, sortable: true, sortField: 'name', sortType: 'string' },
  'icon': { label: 'Icon', width: 50, default: true, sortable: true, sortField: 'icon', sortType: 'string' },
  'layer': { label: 'Layer', width: 140, default: true, sortable: true, sortField: 'layerName', sortType: 'string' },
  'subLayer': { label: 'Sub-Layer', width: 140, default: true, sortable: true, sortField: 'subLayerName', sortType: 'string' },
  'category': { label: 'Category', width: 110, default: true, sortable: true, sortField: 'category', sortType: 'string' },
  'quantity': { label: 'Qty', width: 50, default: true, sortable: true, sortField: 'qty', sortType: 'number', align: 'center' },
  'unitCostLow': { label: 'Unit Cost (Low)', width: 90, default: true, sortable: true, sortField: 'unitCostLow', sortType: 'number', align: 'right' },
  'unitCostHigh': { label: 'Unit Cost (High)', width: 90, default: true, sortable: true, sortField: 'unitCostHigh', sortType: 'number', align: 'right' },
  'totalCostLow': { label: 'Total (Low)', width: 90, default: true, sortable: true, sortField: 'totalCostLow', sortType: 'number', align: 'right' },
  'totalCostHigh': { label: 'Total (High)', width: 90, default: true, sortable: true, sortField: 'totalCostHigh', sortType: 'number', align: 'right' },
  'alternatives': { label: 'Alts', width: 50, default: true, sortable: true, sortField: 'alternativesCount', sortType: 'number', align: 'center' },
  'confidence': { label: 'Confidence', width: 70, default: true, sortable: true, sortField: 'confidence', sortType: 'number', align: 'center' },
  'justification': { label: 'Why Selected', width: 220, default: true, sortable: true, sortField: 'justification', sortType: 'string' },
  'useCases': { label: 'Use Cases', width: 60, default: true, sortable: true, sortField: 'useCaseCount', sortType: 'number', align: 'center' }
};

// ============================================================
// GROUPING OPTIONS
// ============================================================

const GROUPING_OPTIONS = {
  'hierarchy': { name: 'Layer → Sub-Layer → Component', icon: '🌳' },
  'layer': { name: 'By Layer', icon: '📦' },
  'subLayer': { name: 'By Sub-Layer', icon: '📁' },
  'category': { name: 'By Category', icon: '🏷️' },
  'none': { name: 'No Grouping', icon: '🚫' }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function getCategoryForComponent(comp) {
  const name = (comp.name || '').toLowerCase();
  
  if (name.includes('cpu') || name.includes('gpu') || name.includes('tpu') || name.includes('fpga')) return 'Hardware';
  if (name.includes('dram') || name.includes('sram') || name.includes('hbm')) return 'Memory';
  if (name.includes('ssd') || name.includes('hdd') || name.includes('flash')) return 'Storage';
  if (name.includes('ethernet') || name.includes('infiniband') || name.includes('pcie')) return 'Networking';
  if (name.includes('power') || name.includes('cooling') || name.includes('ups')) return 'Power';
  if (name.includes('camera') || name.includes('lidar') || name.includes('sensor')) return 'Sensors';
  if (name.includes('postgres') || name.includes('mysql') || name.includes('mongo') || name.includes('redis')) return 'Database';
  if (name.includes('kafka') || name.includes('airflow') || name.includes('etl')) return 'Pipeline';
  if (name.includes('model') || name.includes('transformer') || name.includes('cnn') || name.includes('xgboost')) return 'ML Model';
  if (name.includes('onnx') || name.includes('tensorrt') || name.includes('pytorch')) return 'Framework';
  if (name.includes('docker') || name.includes('kubernetes')) return 'Container';
  if (name.includes('react') || name.includes('streamlit') || name.includes('dashboard')) return 'Frontend';
  if (name.includes('fastapi') || name.includes('django') || name.includes('node')) return 'Backend';
  if (name.includes('grafana') || name.includes('prometheus') || name.includes('shap')) return 'Monitoring';
  if (name.includes('oauth') || name.includes('rbac') || name.includes('firewall')) return 'Security';
  if (name.includes('audit') || name.includes('compliance') || name.includes('governance')) return 'Governance';
  
  return 'Other';
}

// ============================================================
// BUILD FULL HIERARCHY
// ============================================================

function buildFullHierarchy(components) {
  const tree = [];
  const layerMap = new Map();
  const subLayerMap = new Map();

  components.forEach(comp => {
    const layerName = comp.layerName || 'Unknown Layer';
    const subLayerName = comp.subLayerName || 'Unknown Sub-Layer';

    if (!layerMap.has(layerName)) {
      const layerNode = {
        id: `layer-${layerName}`,
        name: layerName,
        type: 'layer',
        icon: '📦',
        depth: 0,
        expanded: true,
        children: [],
        componentCount: 0,
        totalCostLow: 0,
        totalCostHigh: 0
      };
      layerMap.set(layerName, layerNode);
      tree.push(layerNode);
    }
    const layerNode = layerMap.get(layerName);

    const subLayerKey = `${layerName}::${subLayerName}`;
    if (!subLayerMap.has(subLayerKey)) {
      const subLayerNode = {
        id: `sublayer-${subLayerKey}`,
        name: subLayerName,
        type: 'subLayer',
        icon: '📁',
        depth: 1,
        expanded: false,
        children: [],
        componentCount: 0,
        totalCostLow: 0,
        totalCostHigh: 0
      };
      subLayerMap.set(subLayerKey, subLayerNode);
      layerNode.children.push(subLayerNode);
    }
    const subLayerNode = subLayerMap.get(subLayerKey);

    subLayerNode.children.push({
      ...comp,
      type: 'component',
      depth: 2,
      expanded: false,
      children: []
    });

    subLayerNode.componentCount++;
    subLayerNode.totalCostLow += comp.totalCostLow || 0;
    subLayerNode.totalCostHigh += comp.totalCostHigh || 0;
    layerNode.componentCount++;
    layerNode.totalCostLow += comp.totalCostLow || 0;
    layerNode.totalCostHigh += comp.totalCostHigh || 0;
  });

  return tree;
}

// ============================================================
// BUILD SIMPLE GROUPING
// ============================================================

function buildSimpleGrouping(components, groupingMode) {
  const tree = [];
  const groupMap = new Map();

  components.forEach(comp => {
    let groupKey, groupName, groupIcon, groupType;
    
    if (groupingMode === 'layer') {
      groupKey = comp.layerName || 'Unknown';
      groupName = comp.layerName || 'Unknown Layer';
      groupIcon = '📦';
      groupType = 'layer';
    } else if (groupingMode === 'subLayer') {
      groupKey = comp.subLayerName || 'Unknown';
      groupName = comp.subLayerName || 'Unknown Sub-Layer';
      groupIcon = '📁';
      groupType = 'subLayer';
    } else {
      groupKey = comp.category || 'Other';
      groupName = comp.category || 'Other';
      groupIcon = '🏷️';
      groupType = 'category';
    }

    if (!groupMap.has(groupKey)) {
      const groupNode = {
        id: `${groupType}-${groupKey}`,
        name: groupName,
        type: groupType,
        icon: groupIcon,
        depth: 0,
        expanded: true,
        children: [],
        componentCount: 0,
        totalCostLow: 0,
        totalCostHigh: 0
      };
      groupMap.set(groupKey, groupNode);
      tree.push(groupNode);
    }
    
    const groupNode = groupMap.get(groupKey);
    groupNode.children.push({
      ...comp,
      type: 'component',
      depth: 1,
      expanded: false,
      children: []
    });
    groupNode.componentCount++;
    groupNode.totalCostLow += comp.totalCostLow || 0;
    groupNode.totalCostHigh += comp.totalCostHigh || 0;
  });

  return tree;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const BOMView = () => {
  const { state, dispatch } = useSolution();
  
  // ALL HOOKS CALLED AT TOP - NO CONDITIONAL HOOKS
  
  const [groupingMode, setGroupingMode] = useState('hierarchy');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(
    Object.entries(COLUMN_DEFINITIONS)
      .filter(([key, col]) => col.default)
      .map(([key]) => key)
  );
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showOnlyWithAlternatives, setShowOnlyWithAlternatives] = useState(false);
  const [density, setDensity] = useState('comfortable');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showRowNumbers, setShowRowNumbers] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  
  const [showAlternativesPanel, setShowAlternativesPanel] = useState(false);
  const [activeComponentForAlternatives, setActiveComponentForAlternatives] = useState(null);
  const [alternativesList, setAlternativesList] = useState([]);
  const [bestAlternative, setBestAlternative] = useState(null);
  
  const [showTraceabilityPanel, setShowTraceabilityPanel] = useState(false);
  const [selectedComponentTrace, setSelectedComponentTrace] = useState(null);
  
  const mainContainerRef = useRef(null);

  // ==================== FULLSCREEN HANDLING ====================
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!(document.fullscreenElement || 
                                  document.webkitFullscreenElement || 
                                  document.mozFullScreenElement || 
                                  document.msFullscreenElement);
      setIsFullscreen(isFullscreenNow);
    };

    // Add event listeners for all browsers
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;

    try {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || 
                                       document.webkitFullscreenElement || 
                                       document.mozFullScreenElement || 
                                       document.msFullscreenElement);

      if (!isCurrentlyFullscreen) {
        // Enter fullscreen with cross-browser support
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        } else {
          // Fallback for older browsers
          console.warn('Fullscreen API not supported');
          dispatch({ 
            type: 'SHOW_TOAST', 
            payload: { type: 'warning', message: 'Fullscreen not supported in this browser' } 
          });
        }
      } else {
        // Exit fullscreen with cross-browser support
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      dispatch({ 
        type: 'SHOW_TOAST', 
        payload: { type: 'error', message: 'Failed to toggle fullscreen' } 
      });
    }
  }, [dispatch]);

  // ==================== COMPONENT ENRICHMENT ====================
  
  const enrichedComponents = useMemo(() => {
    return (state.selectedComponents || []).map(comp => {
      const qty = comp.quantity || 1;
      const unitCostLow = comp.cost?.low || 0;
      const unitCostHigh = comp.cost?.high || 0;
      const alternativesCount = getComponentAlternativesCount(comp.isAlternative ? comp.originalComponent : comp.name);
      
      return {
        ...comp,
        qty,
        unitCostLow,
        unitCostHigh,
        totalCostLow: unitCostLow * qty,
        totalCostHigh: unitCostHigh * qty,
        alternativesCount,
        layerName: comp.layerName || 'Unknown',
        subLayerName: comp.subLayerName || 'Unknown',
        category: getCategoryForComponent(comp),
        status: comp.isAlternative ? 'Alternative' : 'Original',
        costUnit: comp.cost?.unit || 'N/A',
        isAlternative: comp.isAlternative || false,
        originalComponent: comp.originalComponent || '',
        justification: comp.justification || 'Selected based on requirements analysis',
        confidence: comp.confidence || 70,
        useCaseCount: (comp.useCases || []).length,
        requirementsCount: (comp.requirements || []).length
      };
    });
  }, [state.selectedComponents]);

  // ==================== FILTERING ====================
  
  const filteredComponents = useMemo(() => {
    let components = enrichedComponents;
    
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      components = components.filter(c => 
        c.name.toLowerCase().includes(query) ||
        c.layerName.toLowerCase().includes(query) ||
        c.subLayerName.toLowerCase().includes(query)
      );
    }
    
    if (showOnlyWithAlternatives) {
      components = components.filter(c => c.alternativesCount > 0);
    }
    
    if (filterCategory !== 'all') {
      components = components.filter(c => c.category === filterCategory);
    }
    
    return components;
  }, [enrichedComponents, searchTerm, showOnlyWithAlternatives, filterCategory]);

  // ==================== SORTING ====================
  
  const handleSort = useCallback((colKey) => {
    const col = COLUMN_DEFINITIONS[colKey];
    if (!col?.sortable) return;
    
    setSortConfig(prev => {
      if (prev.field === col.sortField) {
        return { field: col.sortField, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field: col.sortField, direction: 'asc' };
    });
  }, []);

  const sortedComponents = useMemo(() => {
    const { field, direction } = sortConfig;
    const colEntry = Object.entries(COLUMN_DEFINITIONS).find(([key, col]) => col.sortField === field);
    const sortType = colEntry ? colEntry[1].sortType : 'string';
    
    return [...filteredComponents].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      
      if (sortType === 'number') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredComponents, sortConfig]);

  // ==================== HIERARCHY TREE ====================
  
  const hierarchyTree = useMemo(() => {
    if (groupingMode === 'hierarchy') {
      return buildFullHierarchy(sortedComponents);
    }
    if (groupingMode === 'none') {
      return [{
        id: 'root',
        name: 'All Components',
        type: 'root',
        icon: '📦',
        depth: 0,
        expanded: true,
        componentCount: sortedComponents.length,
        totalCostLow: sortedComponents.reduce((s, c) => s + c.totalCostLow, 0),
        totalCostHigh: sortedComponents.reduce((s, c) => s + c.totalCostHigh, 0),
        children: sortedComponents.map(c => ({ ...c, type: 'component', depth: 1, expanded: false, children: [] }))
      }];
    }
    return buildSimpleGrouping(sortedComponents, groupingMode);
  }, [sortedComponents, groupingMode]);

  // ==================== AUTO-EXPAND LAYERS ====================
  
  useEffect(() => {
    if (groupingMode === 'hierarchy') {
      const layerIds = new Set();
      hierarchyTree.forEach(layer => {
        layerIds.add(layer.id);
      });
      setExpandedNodes(layerIds);
    }
  }, [groupingMode, hierarchyTree]);

  // ==================== TOTALS ====================
  
  const totals = useMemo(() => {
    return {
      low: filteredComponents.reduce((sum, c) => sum + c.totalCostLow, 0),
      high: filteredComponents.reduce((sum, c) => sum + c.totalCostHigh, 0),
      quantity: filteredComponents.reduce((sum, c) => sum + c.qty, 0),
      alternatives: filteredComponents.filter(c => c.isAlternative).length,
      originals: filteredComponents.filter(c => !c.isAlternative).length,
      withAlternatives: filteredComponents.filter(c => c.alternativesCount > 0).length,
      layers: new Set(filteredComponents.map(c => c.layerName)).size,
      subLayers: new Set(filteredComponents.map(c => c.subLayerName)).size
    };
  }, [filteredComponents]);

  // ==================== TOGGLES ====================
  
  const handleToggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) newSet.delete(nodeId);
      else newSet.add(nodeId);
      return newSet;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const allIds = new Set();
    const collect = (nodes) => {
      nodes.forEach(n => {
        if (n.children?.length > 0) {
          allIds.add(n.id);
          collect(n.children);
        }
      });
    };
    collect(hierarchyTree);
    setExpandedNodes(allIds);
  }, [hierarchyTree]);

  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // ==================== ALTERNATIVES ====================
  
  const handleOpenAlternatives = useCallback((component) => {
    const baseName = component.isAlternative ? component.originalComponent : component.name;
    const alts = getAlternativesForComponent(baseName);
    const best = getBestAlternative(baseName, state.approachConstraints);
    
    setActiveComponentForAlternatives(component);
    setAlternativesList(alts);
    setBestAlternative(best);
    setShowAlternativesPanel(true);
  }, [state.approachConstraints]);

  const handleCloseAlternatives = useCallback(() => {
    setShowAlternativesPanel(false);
    setActiveComponentForAlternatives(null);
    setAlternativesList([]);
    setBestAlternative(null);
  }, []);

  const handleReplaceWithAlternative = useCallback((alternative) => {
    if (!activeComponentForAlternatives) return;
    
    const originalComp = activeComponentForAlternatives;
    const baseName = originalComp.isAlternative ? originalComp.originalComponent : originalComp.name;
    const qty = originalComp.qty || 1;
    
    dispatch({ type: 'REMOVE_COMPONENT', payload: originalComp });
    
    const alternativeComponent = {
      ...originalComp,
      id: `alt-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      name: alternative.name,
      icon: alternative.icon,
      purpose: alternative.description || alternative.purpose || originalComp.purpose,
      specifications: alternative.specifications || originalComp.specifications,
      isAlternative: true,
      originalComponent: baseName,
      cost: alternative.specifications?.costRange || alternative.cost || originalComp.cost,
      quantity: qty
    };
    
    dispatch({ type: 'ADD_COMPONENT', payload: alternativeComponent });
    dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: `Replaced ${originalComp.name} with ${alternative.name}` } });
    
    handleCloseAlternatives();
  }, [activeComponentForAlternatives, dispatch, handleCloseAlternatives]);

  // ==================== REVERT ====================
  
  const handleRevertToOriginal = useCallback((component) => {
    if (!component.isAlternative || !component.originalComponent) return;
    
    const allComponents = getAllComponents();
    const originalComponent = allComponents.find(c => c.name === component.originalComponent);
    
    if (originalComponent) {
      dispatch({ type: 'REMOVE_COMPONENT', payload: component });
      dispatch({ type: 'ADD_COMPONENT', payload: { ...originalComponent, quantity: component.qty || 1 } });
      dispatch({ type: 'SHOW_TOAST', payload: { type: 'info', message: `Reverted to ${originalComponent.name}` } });
    }
  }, [dispatch]);

  // ==================== TRACEABILITY ====================
  
  const handleShowTraceability = useCallback((component) => {
    setSelectedComponentTrace(component);
    setShowTraceabilityPanel(true);
  }, []);

  const handleCloseTraceability = useCallback(() => {
    setShowTraceabilityPanel(false);
    setSelectedComponentTrace(null);
  }, []);

  // ==================== EXPORT ====================
  
  const handleExportCSV = useCallback(() => {
    const headers = visibleColumns.map(col => COLUMN_DEFINITIONS[col].label);
    const rows = filteredComponents.map(comp => {
      return visibleColumns.map(colKey => {
        switch (colKey) {
          case 'name': return comp.name;
          case 'icon': return comp.icon || '';
          case 'layer': return comp.layerName;
          case 'subLayer': return comp.subLayerName;
          case 'category': return comp.category;
          case 'quantity': return comp.qty;
          case 'unitCostLow': return comp.unitCostLow;
          case 'unitCostHigh': return comp.unitCostHigh;
          case 'totalCostLow': return comp.totalCostLow;
          case 'totalCostHigh': return comp.totalCostHigh;
          case 'alternatives': return comp.alternativesCount;
          case 'confidence': return comp.confidence;
          case 'justification': return comp.justification;
          case 'useCases': return comp.useCaseCount;
          default: return '';
        }
      });
    });
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bom-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    dispatch({ type: 'SHOW_TOAST', payload: { type: 'success', message: 'BOM exported to CSV' } });
  }, [filteredComponents, visibleColumns, dispatch]);

  // ==================== SORT INDICATOR ====================
  
  const getSortIndicator = (colKey) => {
    const col = COLUMN_DEFINITIONS[colKey];
    if (!col?.sortable) return null;
    
    const isActive = sortConfig.field === col.sortField;
    
    if (isActive) {
      return sortConfig.direction === 'asc' 
        ? <FiArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold inline-block ml-1" />
        : <FiArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold inline-block ml-1" />;
    }
    return <span className="text-gray-300 text-xs font-bold inline-block ml-1">⇅</span>;
  };

  // ==================== RENDER TREE NODE ====================
  
  let rowNumber = 0;
  
  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedNode === node.id;
    const isHovered = hoveredNode === node.id;
    const hasChildren = node.children && node.children.length > 0;
    
    if (node.type === 'component') rowNumber++;
    
    const densityPadding = { compact: 'py-1', comfortable: 'py-2', spacious: 'py-3' };
    
    return (
      <React.Fragment key={node.id}>
        <tr 
          className={`${isSelected ? 'bg-blue-50' : ''} ${isHovered ? 'bg-gray-50' : ''} ${node.type !== 'component' ? 'bg-gradient-to-r from-gray-50 to-white border-t border-gray-200' : 'border-b border-gray-50'} transition-colors cursor-pointer`}
          onClick={() => { setSelectedNode(node.id); if (hasChildren) handleToggleNode(node.id); }}
          onMouseEnter={() => setHoveredNode(node.id)}
          onMouseLeave={() => setHoveredNode(null)}
        >
          {showRowNumbers && (
            <td className="px-2 py-2 text-xs text-gray-400 text-center w-8">
              {node.type === 'component' ? rowNumber : ''}
            </td>
          )}
          
          {/* Name */}
          <td className={`px-2 ${densityPadding[density]} whitespace-nowrap`} style={{ minWidth: 250 }}>
            <div className="flex items-center" style={{ paddingLeft: `${level * 20}px` }}>
              {hasChildren ? (
                <button onClick={(e) => { e.stopPropagation(); handleToggleNode(node.id); }} className="mr-1 p-0.5 hover:bg-gray-200 rounded">
                  {isExpanded ? <FiChevronDown className="w-4 h-4 text-gray-600" /> : <FiChevronRight className="w-4 h-4 text-gray-600" />}
                </button>
              ) : <span className="mr-1 w-5" />}
              <span className="mr-2 text-lg">{node.icon}</span>
              <span className={node.type === 'component' ? 'font-medium text-gray-700' : 'font-semibold text-gray-800'}>
                {node.name}
              </span>
              {node.type === 'component' && node.isAlternative && (
                <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-semibold">ALT</span>
              )}
            </div>
          </td>
          
          {/* Dynamic Columns */}
          {visibleColumns.filter(key => key !== 'name').map(colKey => {
            switch (colKey) {
              case 'icon':
                return <td key={colKey} className="px-3 py-2 text-center"><span className="text-lg">{node.icon}</span></td>;
              case 'layer':
                return <td key={colKey} className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{node.type === 'component' ? node.layerName : ''}</td>;
              case 'subLayer':
                return <td key={colKey} className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">{node.type === 'component' ? node.subLayerName : ''}</td>;
              case 'category':
                return <td key={colKey} className="px-3 py-2 text-sm">{node.type === 'component' ? <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{node.category}</span> : ''}</td>;
              case 'quantity':
                return <td key={colKey} className="px-3 py-2 text-sm text-center">{node.type === 'component' ? <span className="font-bold">{node.qty}</span> : <span className="font-semibold text-gray-500">{node.componentCount || ''}</span>}</td>;
              case 'unitCostLow':
                return <td key={colKey} className="px-3 py-2 text-sm text-right">{node.type === 'component' ? `$${node.unitCostLow.toFixed(0)}` : ''}</td>;
              case 'unitCostHigh':
                return <td key={colKey} className="px-3 py-2 text-sm text-right">{node.type === 'component' ? `$${node.unitCostHigh.toFixed(0)}` : ''}</td>;
              case 'totalCostLow':
                return <td key={colKey} className="px-3 py-2 text-sm text-right">{node.type === 'component' ? <span className="font-semibold text-green-600">${node.totalCostLow.toFixed(0)}</span> : <span className="font-semibold text-green-700">${node.totalCostLow?.toFixed(0) || ''}</span>}</td>;
              case 'totalCostHigh':
                return <td key={colKey} className="px-3 py-2 text-sm text-right">{node.type === 'component' ? <span className="font-semibold text-green-600">${node.totalCostHigh.toFixed(0)}</span> : <span className="font-semibold text-green-700">${node.totalCostHigh?.toFixed(0) || ''}</span>}</td>;
              case 'alternatives':
                return (
                  <td key={colKey} className="px-3 py-2 text-sm text-center">
                    {node.type === 'component' && node.alternativesCount > 0 ? (
                      <button onClick={(e) => { e.stopPropagation(); handleOpenAlternatives(node); }} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold hover:bg-purple-200 cursor-pointer">
                        <FiGitBranch className="w-3 h-3" />{node.alternativesCount}
                      </button>
                    ) : node.type !== 'component' ? <span className="text-xs text-gray-400">{node.componentCount || ''}</span> : <span className="text-gray-300">—</span>}
                  </td>
                );
              case 'confidence':
                return (
                  <td key={colKey} className="px-3 py-2 text-sm text-center">
                    {node.type === 'component' ? (
                      <span className={`font-bold ${node.confidence > 80 ? 'text-green-600' : node.confidence > 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {node.confidence}%
                      </span>
                    ) : ''}
                  </td>
                );
              case 'justification':
                return (
                  <td key={colKey} className="px-3 py-2 text-xs text-gray-600 max-w-[220px] truncate" title={node.justification}>
                    {node.type === 'component' ? node.justification || '—' : ''}
                  </td>
                );
              case 'useCases':
                return (
                  <td key={colKey} className="px-3 py-2 text-sm text-center">
                    {node.type === 'component' && node.useCaseCount > 0 ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{node.useCaseCount}</span>
                    ) : node.type !== 'component' ? '' : <span className="text-gray-300">—</span>}
                  </td>
                );
              default:
                return null;
            }
          })}
          
          {/* Actions */}
          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
            {node.type === 'component' && (
              <div className="flex items-center justify-center gap-1.5">
                {node.alternativesCount > 0 && (
                  <button onClick={() => handleOpenAlternatives(node)} className="p-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100" title="Replace with alternative">
                    <FiRefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
                {node.isAlternative && node.originalComponent && (
                  <button onClick={() => handleRevertToOriginal(node)} className="p-1.5 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100" title="Revert to original">
                    <FiArrowUpCircle className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => handleShowTraceability(node)} className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" title="View traceability">
                  <FiActivity className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </td>
        </tr>
        
        {isExpanded && hasChildren && node.children.map(child => renderTreeNode(child, level + 1))}
      </React.Fragment>
    );
  };

  // ==================== RENDER ====================

  return (
    <div 
      ref={mainContainerRef} 
      className={`h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><FiBox className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Bill of Materials</h2>
                <p className="text-xs text-gray-500">{totals.layers} layers • {totals.subLayers} sub-layers • {filteredComponents.length} components</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={handleExpandAll} className="p-2 hover:bg-gray-100 rounded-lg" title="Expand All">
                <FiChevronsRight className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={handleCollapseAll} className="p-2 hover:bg-gray-100 rounded-lg" title="Collapse All">
                <FiChevronsLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <button onClick={() => setShowSettings(!showSettings)} className="p-2 hover:bg-gray-100 rounded-lg" title="Toggle Columns">
                <FiColumns className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                onClick={toggleFullscreen} 
                className="p-2 hover:bg-gray-100 rounded-lg" 
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? 
                  <FiMinimize2 className="w-4 h-4 text-gray-600" /> : 
                  <FiMaximize2 className="w-4 h-4 text-gray-600" />
                }
              </button>
              <button 
                onClick={() => setDensity(d => d === 'comfortable' ? 'compact' : d === 'compact' ? 'spacious' : 'comfortable')} 
                className="p-2 hover:bg-gray-100 rounded-lg" 
                title={`Toggle Density (Current: ${density})`}
              >
                {density === 'compact' ? 
                  <FiMinimize className="w-4 h-4 text-gray-600" /> : 
                  density === 'comfortable' ?
                  <FiMaximize className="w-4 h-4 text-gray-600" /> :
                  <FiMaximize2 className="w-4 h-4 text-gray-600" />
                }
              </button>
              <button onClick={handleExportCSV} className="p-2 hover:bg-green-50 rounded-lg" title="Export CSV">
                <FiDownload className="w-4 h-4 text-green-600" />
              </button>
            </div>
          </div>
          
          <AnimatePresence>
            {showSettings && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-b border-gray-200">
                <div className="p-4 bg-gray-50">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(COLUMN_DEFINITIONS).map(([key, col]) => (
                      <label key={key} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={visibleColumns.includes(key)} 
                          onChange={(e) => {
                            if (e.target.checked) setVisibleColumns(prev => [...prev, key]);
                            else setVisibleColumns(prev => prev.filter(c => c !== key));
                          }} 
                          className="w-3.5 h-3.5 text-blue-600" 
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3 p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Search components..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
              />
            </div>
            <select 
              value={groupingMode} 
              onChange={(e) => setGroupingMode(e.target.value)} 
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(GROUPING_OPTIONS).map(([key, opt]) => <option key={key} value={key}>{opt.icon} {opt.name}</option>)}
            </select>
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)} 
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {[...new Set(enrichedComponents.map(c => c.category))].map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input 
                type="checkbox" 
                checked={showOnlyWithAlternatives} 
                onChange={(e) => setShowOnlyWithAlternatives(e.target.checked)} 
                className="w-3.5 h-3.5 text-purple-600" 
              />
              With Alternatives
            </label>
          </div>
        </div>
        
        {/* BOM Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                  {showRowNumbers && <th className="px-2 py-2.5 text-[10px] font-bold text-gray-500 uppercase w-8">#</th>}
                  <th onClick={() => handleSort('name')} className={`px-3 py-2.5 text-left text-[10px] font-bold uppercase cursor-pointer hover:bg-gray-200 select-none ${sortConfig.field === 'name' ? 'text-blue-700 bg-blue-50' : 'text-gray-600'}`} style={{ minWidth: 250 }}>
                    <span className="flex items-center gap-1">Component Name{getSortIndicator('name')}</span>
                  </th>
                  {visibleColumns.filter(key => key !== 'name').map(colKey => {
                    const col = COLUMN_DEFINITIONS[colKey];
                    const isActive = sortConfig.field === col.sortField;
                    return (
                      <th key={colKey} onClick={() => handleSort(colKey)} className={`px-3 py-2.5 text-[10px] font-bold uppercase cursor-pointer hover:bg-gray-200 whitespace-nowrap select-none ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${isActive ? 'text-blue-700 bg-blue-50' : 'text-gray-600'}`} style={{ minWidth: col.width }}>
                        <span className="flex items-center gap-1">{col.label}{getSortIndicator(colKey)}</span>
                      </th>
                    );
                  })}
                  <th className="px-3 py-2.5 text-center text-[10px] font-bold text-gray-600 uppercase w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hierarchyTree.map(node => renderTreeNode(node, 0))}
              </tbody>
            </table>
          </div>
        </div>
        
        {filteredComponents.length === 0 && (
          <div className="text-center p-12 bg-white rounded-xl border border-gray-200 mt-3">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-gray-600 font-medium">No components found</p>
          </div>
        )}
      </div>
      
      {/* Alternatives Panel */}
      <AnimatePresence>
        {showAlternativesPanel && activeComponentForAlternatives && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="fixed inset-0 bg-black/30 z-40" 
              onClick={handleCloseAlternatives} 
            />
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }} 
              transition={{ type: 'spring', damping: 25 }} 
              className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col"
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Alternatives</h3>
                  <p className="text-sm text-gray-500">{activeComponentForAlternatives.name}</p>
                </div>
                <button onClick={handleCloseAlternatives} className="p-2 hover:bg-gray-100 rounded-lg">
                  <FiXCircle className="w-5 h-5" />
                </button>
              </div>
              {bestAlternative && (
                <div className="p-4 bg-green-50 border-b border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <FiStar className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-bold text-green-800">RECOMMENDED</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{bestAlternative.icon}</span>
                      <div>
                        <div className="font-semibold">{bestAlternative.name}</div>
                        <div className="text-xs text-gray-500">
                          {bestAlternative.specifications?.costRange ? 
                            `$${bestAlternative.specifications.costRange.low} - $${bestAlternative.specifications.costRange.high}` : 
                            'Cost N/A'}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleReplaceWithAlternative(bestAlternative)} className="w-full py-2 bg-green-500 text-white rounded-lg font-semibold text-sm hover:bg-green-600">
                      Use This
                    </button>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4">
                <h4 className="text-sm font-semibold mb-3">All ({alternativesList.length})</h4>
                <div className="space-y-2">
                  {alternativesList.map(alt => (
                    <div key={alt.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{alt.icon}</span>
                        <div className="flex-1">
                          <div className="font-semibold text-sm">{alt.name}</div>
                          <div className="text-xs text-gray-500">
                            {alt.specifications?.costRange ? 
                              `$${alt.specifications.costRange.low} - $${alt.specifications.costRange.high}` : 
                              'Cost N/A'}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleReplaceWithAlternative(alt)} className="w-full py-1.5 bg-purple-500 text-white rounded text-xs font-semibold hover:bg-purple-600">
                        Replace
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Traceability Panel */}
      <AnimatePresence>
        {showTraceabilityPanel && selectedComponentTrace && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="fixed inset-0 bg-black/30 z-40" 
              onClick={handleCloseTraceability} 
            />
            <motion.div 
              initial={{ x: '100%' }} 
              animate={{ x: 0 }} 
              exit={{ x: '100%' }} 
              transition={{ type: 'spring', damping: 25 }} 
              className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col"
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
                <div>
                  <h3 className="text-lg font-bold">Traceability</h3>
                  <p className="text-sm text-gray-500">{selectedComponentTrace.name}</p>
                </div>
                <button onClick={handleCloseTraceability} className="p-2 hover:bg-gray-100 rounded-lg">
                  <FiXCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-xs font-bold text-green-800 mb-1">CONFIDENCE</h4>
                  <div className="text-3xl font-bold text-green-600">{selectedComponentTrace.confidence || 70}%</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-xs font-bold text-blue-800 mb-1">WHY SELECTED</h4>
                  <p className="text-sm text-blue-700">{selectedComponentTrace.justification || 'Selected based on requirements'}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="text-xs font-bold text-purple-800 mb-1">USE CASES</h4>
                  <p className="text-sm text-purple-700">{selectedComponentTrace.useCaseCount || 0} use cases served</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-xs font-bold text-amber-800 mb-1">COST JUSTIFICATION</h4>
                  <p className="text-sm text-amber-700">{selectedComponentTrace.costJustification || 'Cost within budget'}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="text-xs font-bold text-red-800 mb-1">RISK ASSESSMENT</h4>
                  {(selectedComponentTrace.risk || []).map((r, i) => (
                    <p key={i} className="text-sm text-red-700">⚠ {r.description}</p>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BOMView;